import Lenis from '@theme/vendor-lenis';
import { prefersReducedMotion, debounce } from '@theme/utilities';

/** @typedef {import('gsap').gsap} GSAP */
/** @typedef {typeof import('gsap/ScrollTrigger').ScrollTrigger} ScrollTriggerPlugin */

/**
 * @typedef {Object} GrScrollToOptions
 * @property {number} [offset]
 * @property {boolean} [immediate]
 * @property {number} [duration]
 * @property {boolean} [lock]
 * @property {boolean} [force]
 * @property {(instance: import('@theme/vendor-lenis').default) => void} [onComplete]
 */

/**
 * Coordinates Lenis smooth scrolling with GSAP ScrollTrigger.
 * Singleton — use GrScrollEngine.get() after gr-scroll-init runs.
 */
export class GrScrollEngine {
  /** @type {GrScrollEngine | null} */
  static #instance = null;

  /** @type {GSAP | null} */
  #gsap = null;

  /** @type {ScrollTriggerPlugin | null} */
  #ScrollTrigger = null;

  /** @type {import('@theme/vendor-lenis').default | null} */
  #lenis = null;

  /** @type {boolean} */
  #smooth = false;

  /** @type {boolean} */
  #started = true;

  /** @type {number} */
  #lockCount = 0;

  /** @type {(() => void) | null} */
  #tickerCallback = null;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedRefresh = null;

  /** @type {(() => void) | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #destroyed = false;

  /** @type {boolean} */
  #isCoarse = false;

  /** @type {number} */
  #lastWinW = 0;

  /** @type {number} */
  #lastWinH = 0;

  /** @type {(() => void) | null} */
  #onWindowResize = null;

  /**
   * @param {Object} options
   * @param {GSAP} options.gsap
   * @param {ScrollTriggerPlugin} options.ScrollTrigger
   * @param {boolean} [options.smooth]
   */
  constructor({ gsap, ScrollTrigger, smooth = true }) {
    if (GrScrollEngine.#instance) {
      return GrScrollEngine.#instance;
    }

    this.#gsap = gsap;
    this.#ScrollTrigger = ScrollTrigger;
    this.#smooth = smooth;

    gsap.registerPlugin(ScrollTrigger);

    GrScrollEngine.#instance = this;
  }

  /** @returns {GrScrollEngine | null} */
  static get() {
    return GrScrollEngine.#instance;
  }

  get gsap() {
    return this.#gsap;
  }

  get ScrollTrigger() {
    return this.#ScrollTrigger;
  }

  get lenis() {
    return this.#lenis;
  }

  get isSmooth() {
    return this.#smooth && Boolean(this.#lenis);
  }

  get isStarted() {
    return this.#started;
  }

  /**
   * Initialize Lenis + ScrollTrigger sync and global listeners.
   * @param {Object} [options]
   * @param {boolean} [options.manualScrollRestoration]
   */
  init({ manualScrollRestoration = true } = {}) {
    if (this.#destroyed || !this.#gsap || !this.#ScrollTrigger) return;

    const html = document.documentElement;

    html.classList.add('gr-scroll-enabled');
    html.classList.toggle('gr-scroll-native', !this.#smooth);

    if (manualScrollRestoration && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Pin pull-up / fade heights must not track live dvh — URL-bar show/hide
    // was shifting the landing-story section mid-scroll on iOS/Android.
    this.#syncLayoutVh();

    // iOS URL-bar show/hide must not refresh triggers — that reflows blur text and flickers.
    this.#ScrollTrigger.config({ ignoreMobileResize: true });

    if (this.#smooth) {
      this.#initLenis();
    }

    this.#bindGlobalEvents();
    this.#ScrollTrigger.refresh(false);
  }

  /** Freeze layout viewport height so CSS pull-ups stay aligned with the pin. */
  #syncLayoutVh() {
    const h = Math.round(window.innerHeight);
    if (h > 0) {
      document.documentElement.style.setProperty('--gr-layout-vh', `${h}px`);
    }
  }

  #initLenis() {
    const html = document.documentElement;

    this.#lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
      autoRaf: false,
    });

    html.classList.add('lenis', 'lenis-smooth');

    this.#lenis.on('scroll', this.#ScrollTrigger.update);

    this.#tickerCallback = (time) => {
      this.#lenis?.raf(time * 1000);
    };

    this.#gsap.ticker.add(this.#tickerCallback);
    this.#gsap.ticker.lagSmoothing(0);
  }

  #bindGlobalEvents() {
    this.#debouncedRefresh = debounce(() => {
      if (this.#destroyed) return;
      this.refresh();
    }, 200);

    this.#isCoarse =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none), (pointer: coarse)').matches;

    this.#lastWinW = window.innerWidth;
    this.#lastWinH = window.innerHeight;

    // iOS URL-bar show/hide fires resize with height-only change — refreshing
    // ScrollTrigger mid-scroll snaps pinned sections back to the start.
    this.#onWindowResize = () => {
      if (this.#destroyed) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (this.#isCoarse) {
        const widthChanged = Math.abs(w - this.#lastWinW) > 1;
        const heightDelta = Math.abs(h - this.#lastWinH);
        this.#lastWinW = w;
        this.#lastWinH = h;
        // Ignore chrome-only height jitter; still refresh on orientation/width.
        if (!widthChanged && heightDelta < 150) return;
        this.#syncLayoutVh();
      } else {
        this.#lastWinW = w;
        this.#lastWinH = h;
        this.#syncLayoutVh();
      }
      this.#debouncedRefresh?.();
    };

    window.addEventListener('resize', this.#onWindowResize);

    // Avoid ResizeObserver refresh on touch: iOS chrome show/hide resizes body
    // and was re-scrubbing blur text opacity (visible flicker).
    if (!this.#isCoarse && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(this.#debouncedRefresh);
      ro.observe(document.body);
      this.#resizeObserver = () => ro.disconnect();
    }

    document.addEventListener('shopify:section:load', this.#debouncedRefresh);
    document.addEventListener('shopify:section:reorder', this.#debouncedRefresh);
    document.addEventListener('shopify:block:select', this.#debouncedRefresh);
    document.addEventListener('shopify:block:deselect', this.#debouncedRefresh);

    window.addEventListener('gr-scroll:refresh', this.#debouncedRefresh);
  }

  /** Pause Lenis + GSAP ticker integration. */
  stop() {
    this.#started = false;
    this.#lenis?.stop();
  }

  /** Resume Lenis scrolling. */
  start() {
    this.#started = true;
    this.#lenis?.start();
  }

  /**
   * Lock page scroll (increments a counter for nested overlays).
   */
  lock() {
    this.#lockCount += 1;
    if (this.#lockCount === 1) {
      document.documentElement.classList.add('gr-scroll-locked');
      this.stop();
    }
  }

  /**
   * Unlock page scroll.
   */
  unlock() {
    if (this.#lockCount === 0) return;
    this.#lockCount -= 1;
    if (this.#lockCount === 0) {
      document.documentElement.classList.remove('gr-scroll-locked');
      if (this.#started) this.start();
    }
  }

  /**
   * @param {number | string | HTMLElement} target
   * @param {GrScrollToOptions} [options]
   */
  scrollTo(target, options = {}) {
    if (this.#lenis) {
      if (!this.#started) this.start();
      this.#lenis.scrollTo(target, options);
      return;
    }

    if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior: options.immediate ? 'instant' : 'smooth' });
      return;
    }

    const element = typeof target === 'string' ? document.querySelector(target) : target;
    element?.scrollIntoView({ behavior: options.immediate ? 'instant' : 'smooth' });
  }

  /** Recalculate ScrollTrigger positions. */
  refresh() {
    this.#ScrollTrigger?.refresh(false);
  }

  /** Destroy the engine and restore native scrolling. */
  destroy() {
    if (this.#destroyed) return;
    this.#destroyed = true;

    if (this.#debouncedRefresh) {
      window.removeEventListener('resize', this.#onWindowResize);
      document.removeEventListener('shopify:section:load', this.#debouncedRefresh);
      document.removeEventListener('shopify:section:reorder', this.#debouncedRefresh);
      document.removeEventListener('shopify:block:select', this.#debouncedRefresh);
      document.removeEventListener('shopify:block:deselect', this.#debouncedRefresh);
      window.removeEventListener('gr-scroll:refresh', this.#debouncedRefresh);
    }

    this.#resizeObserver?.();

    if (this.#tickerCallback && this.#gsap) {
      this.#gsap.ticker.remove(this.#tickerCallback);
    }

    this.#lenis?.destroy();
    this.#lenis = null;

    this.#ScrollTrigger?.getAll().forEach((trigger) => trigger.kill());
    this.#ScrollTrigger?.clearMatchMedia();

    const html = document.documentElement;
    html.classList.remove('gr-scroll-enabled', 'gr-scroll-native', 'gr-scroll-locked', 'lenis', 'lenis-smooth');

    GrScrollEngine.#instance = null;
  }
}

/**
 * Boot the scroll engine when the page opts in via data-gr-scroll.
 * GSAP is loaded globally via vendor scripts (see snippets/gr-scroll.liquid).
 */
export function initGrScrollEngine() {
  if (!document.documentElement.hasAttribute('data-gr-scroll')) return null;
  if (GrScrollEngine.get()) return GrScrollEngine.get();

  const gsap = /** @type {GSAP | undefined} */ (window.gsap);
  const ScrollTrigger = /** @type {ScrollTriggerPlugin | undefined} */ (window.ScrollTrigger);

  if (!gsap || !ScrollTrigger) {
    return null;
  }

  const smooth = !prefersReducedMotion();

  const engine = new GrScrollEngine({ gsap, ScrollTrigger, smooth });
  engine.init();

  window.dispatchEvent(new CustomEvent('gr-scroll:ready', { detail: { engine } }));

  return engine;
}

/**
 * Wait for GSAP globals, then boot the scroll engine (handles script order races).
 * @returns {Promise<GrScrollEngine | null>}
 */
export function bootGrScrollEngine() {
  if (!document.documentElement.hasAttribute('data-gr-scroll')) {
    return Promise.resolve(null);
  }

  const existing = GrScrollEngine.get();
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 100;

    const tryInit = () => {
      const engine = initGrScrollEngine();
      if (engine) {
        resolve(engine);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        console.warn('[gr-scroll] GSAP or ScrollTrigger not loaded — scroll engine skipped.');
        resolve(null);
        return;
      }

      requestAnimationFrame(tryInit);
    };

    tryInit();
  });
}

if (typeof window !== 'undefined') {
  window.GrScroll = {
    get: () => GrScrollEngine.get(),
    init: initGrScrollEngine,
    boot: () => bootGrScrollEngine(),
    refresh: () => window.dispatchEvent(new CustomEvent('gr-scroll:refresh')),
  };

  if (document.documentElement.hasAttribute('data-gr-scroll')) {
    bootGrScrollEngine();
  }
}
